import {
    Text,
    Flex,
    Icon,
    Box,
    HStack,
    useToast,
} from '@chakra-ui/react';
import React from 'react';
import { TbAlertTriangle } from 'react-icons/tb';
import axios from 'axios';
import EmsExportDayTrack from '../../../components/order/EmsExportDayTrack';
import PartBayButton from '../../../components/PartBayButton';
import type { OrderEmsSummary } from '../../../hooks/useGetOrderDetail';
import { useSyncEmsListPrices } from '../../../hooks/useSyncEmsListPrices';
import { SIDE_DRAWER_CONTAINER_PROPS, INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../../../modals/sideDrawerContainerProps';
import AppDrawer from '../../../components/AppDrawer';

interface ExportHistoryDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    ems: OrderEmsSummary;
    orderId: string;
    onSynced: () => void | Promise<unknown>;
    hideOverlay?: boolean;
}

function syncPricesErrorMessage(error: unknown): string {
    if (!axios.isAxiosError(error)) {
        return 'Failed to sync EMS list prices. Please try again.';
    }
    switch (error.response?.status) {
        case 403:
            return 'You do not have permission to sync EMS list prices for this order.';
        case 404:
            return 'Order not found.';
        case 409:
            return 'This order has no EMS file to sync from.';
        default:
            return 'Failed to sync EMS list prices. Please try again.';
    }
}

const ExportHistoryDrawer = ({
    isOpen,
    onClose,
    ems,
    orderId,
    onSynced,
    hideOverlay = false,
}: ExportHistoryDrawerProps) => {
    const toast = useToast();
    const syncEmsListPrices = useSyncEmsListPrices();

    const handleSyncToQuote = () => {
        syncEmsListPrices.mutate(
            { orderId },
            {
                onSuccess: async () => {
                    await onSynced();
                },
                onError: (error) => {
                    toast({
                        title: 'Sync failed',
                        description: syncPricesErrorMessage(error),
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            title="Export History"
            hideOverlay={hideOverlay}
            contentProps={{
                overflow: 'hidden',
                maxW: '860px',
                containerProps: hideOverlay ? SIDE_DRAWER_CONTAINER_PROPS : undefined,
                motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS,
            }}
            bodyProps={{ px: 5, py: 4, pt: 4 }}
            headerBelow={
                !ems.matchesLatestEmsPrices ? (
                    <Flex
                        px={5}
                        py={3}
                        align="center"
                        justify="space-between"
                        gap={3}
                        borderBottom="1px solid"
                        borderColor="orange.100"
                        bg="orange.50"
                        flexShrink={0}
                    >
                        <HStack spacing={2} align="flex-start" minW={0}>
                            <Icon
                                as={TbAlertTriangle as React.ElementType}
                                color="orange.500"
                                boxSize={4}
                                mt={0.5}
                                flexShrink={0}
                            />
                            <Text fontSize="sm" color="gray.700" lineHeight="1.35">
                                New Parts estimated list prices do not match the latest EMS. Sync to update them before quoting.
                            </Text>
                        </HStack>
                        <Box flexShrink={0} sx={{ '& > button': { minW: '6.75rem' } }}>
                            <PartBayButton
                                colorScheme="partbay-blue"
                                variant="solid"
                                size="sm"
                                text="Sync"
                                onClick={handleSyncToQuote}
                                isLoading={syncEmsListPrices.isPending}
                                loadingText="Syncing"
                            />
                        </Box>
                    </Flex>
                ) : undefined
            }
        >
            <EmsExportDayTrack pushes={ems.pushes} />
        </AppDrawer>
    );
};

export default ExportHistoryDrawer;
